import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { Delta } from 'quill/core';
import React from 'react';
import * as styles from './CardContent.module.scss';
import prepareContentForCard from './prepareContentForCard';
import useLegacyIsLoggedIn from '../../account/useIsLoggedIn';
import Button, { ButtonWithLoadingState } from '../../components/Button';
import DeltaPreview from '../../components/DeltaPreview';
import { gql } from '../../graphql/generated/gql';
import cardErrorBanner from '../../images/editor/card-error-banner.png';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from '../../post/postPolicyCheck';
import useEmbedAuthListener from '../../post/useEmbedAuthListener';
import useRequestPostAccess from '../../post/useRequestPostAccess';
import { parseShareSecretFromParams } from '../../post/useShareSecret';
import { UserRole } from '../../topic/topicPolicyCheck';
import { getFirstLineFromDelta } from '../../utils/deltaHelpers';
import Link from '../../utils/router/Link';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';
import { useQuillReadOnly } from '../QuillContext';
import { parseMentionId } from '../utils/matchMention';

gql(`
  fragment CardPostContent_post on PostFields {
    id
    content {
      id
      delta
    }
    ... on PublicPost {
      banner {
        original
      }
    }
    ... on Post {
      banner {
        original
      }
    }
    ...isAllowedThroughLinkAccess_post
    ...postPath_post
    ...publicPostPath_post
  }
`);

const CARD_POST_CONTENT_QUERY = gql(/** @complexity 4 */ `
  query cardPostContent($id: ID!, $shareSecret: String!) {
    post(id: $id, shareSecret: $shareSecret) {
      id
      ...CardPostContent_post
    }
  }
`);

const CARD_PUBLIC_POST_CONTENT_QUERY = gql(/** @complexity 4 */ `
  query cardPublicPostContent($id: ID!, $shareSecret: String!) {
    publicPost(id: $id, shareSecret: $shareSecret) {
      id
      ...CardPostContent_post
    }
  }
`);

function hasBlankTitle(delta: Delta | null): boolean {
  if (!delta) return false;
  const firstLine = getFirstLineFromDelta(delta);
  return firstLine?.length() === 0;
}

interface CardContentBannerProps {
  url: string;
}

const CardContentBanner = ({ url }: CardContentBannerProps) => (
  <div className={styles.banner} style={{ backgroundImage: `url(${url})` }} />
);

interface CardContainerProps {
  children: React.ReactNode;
  to?: string;
  className?: string;
}

const CardContainer = ({ className, children, to }: CardContainerProps) => (
  <div className={classNames('card-content', className)}>
    <div
      className={classNames(styles.container, { [styles.isLink]: to != null })}
    >
      {to ? (
        <Link to={to}>
          <div className={styles.contentContainer}>{children}</div>
        </Link>
      ) : (
        <div className={styles.contentContainer}>{children}</div>
      )}
    </div>
  </div>
);

interface CardInnerContainerProps {
  children: React.ReactNode;
  isTitleBlank?: boolean;
}

const CardInnerContainer = ({
  children,
  isTitleBlank,
}: CardInnerContainerProps) => {
  return (
    <div
      className={classNames(styles.innerContainer, {
        [styles.isTitleBlank]: isTitleBlank,
      })}
    >
      {children}
    </div>
  );
};

interface CardErrorProps {
  title: string;
  message: string;
  buttons: React.ReactNode;
}

const CardError = ({ title, message, buttons }: CardErrorProps) => {
  return (
    <CardContainer className={styles.cardError}>
      <CardInnerContainer>
        <div className={styles.cardErrorTitle}>{title}</div>
        <div className={styles.cardErrorMessageContainer}>
          <div className={styles.cardErrorMessage}>{message}</div>
        </div>

        <div className={styles.cardErrorButtons}>{buttons}</div>
      </CardInnerContainer>
      <CardContentBanner url={cardErrorBanner} />
    </CardContainer>
  );
};

interface RequestAccessCardErrorProps {
  postId: string;
}

const RequestAccessCardError = ({ postId }: RequestAccessCardErrorProps) => {
  const path = postPath({ title: '', id: postId });
  const { requestAccess, hasRequestedAccess, loading } = useRequestPostAccess({
    postId,
  });

  return (
    <CardError
      title="Post not visible"
      message="You do not have permission to view this post."
      buttons={
        <>
          <Button
            to={path}
            size="small"
            variant="secondary"
            target="blank"
            rel="noopener noreferrer"
          >
            Open tab
          </Button>
          <ButtonWithLoadingState
            size="small"
            variant="primary"
            onClick={() => requestAccess()}
            disabled={hasRequestedAccess}
            loading={loading}
          >
            {hasRequestedAccess ? 'Requested' : 'Request access'}
          </ButtonWithLoadingState>
        </>
      }
    />
  );
};

const NotFoundError = () => {
  return (
    <CardError
      title="Post no longer exists"
      message="This post has been deleted and is no longer accessible."
      buttons={
        <Button
          to="https://go.slab.com/help-deleting-posts"
          size="small"
          variant="secondary"
          target="blank"
          rel="noopener noreferrer"
        >
          Learn about posts
        </Button>
      }
    />
  );
};

interface NotLoggedInCardErrorProps {
  postId: string;
}

const NotLoggedInCardError = ({ postId }: NotLoggedInCardErrorProps) => {
  const path = postPath({ title: '', id: postId });
  useEmbedAuthListener();
  const isInIframe = window.self !== window.top;

  return (
    <CardError
      title="Post not publicly visible"
      message="You may need to log in or ask the owner to make the post public."
      buttons={
        <>
          <Button
            to={path}
            size="small"
            variant="secondary"
            target="blank"
            rel="noopener noreferrer"
          >
            Open tab
          </Button>
          <Button
            size="small"
            variant="primary"
            {...(isInIframe
              ? {
                  onClick: () => {
                    window.open(
                      '/embed/login',
                      '_blank',
                      'width=480,height=600',
                    );
                  },
                }
              : {
                  to: `/login?redirect=${encodeURIComponent(
                    window.location.pathname,
                  )}`,
                })}
          >
            Login to Slab
          </Button>
        </>
      }
    />
  );
};

interface CardContentProps {
  id: string;
  params?: string;
}

const CardContent = ({ id, params }: CardContentProps) => {
  const readOnly = useQuillReadOnly();
  const shareSecret = parseShareSecretFromParams(params) ?? id;

  const isLoggedIn = useLegacyIsLoggedIn();

  const isPublicView = useIsPublicView();
  const { id: postId, anchor } = parseMentionId(id);

  const {
    data,
    loading: isInternalPostLoading,
    error: internalError,
  } = useQuery(CARD_POST_CONTENT_QUERY, {
    variables: { id: postId, shareSecret },
    skip: isPublicView,
  });

  const {
    data: publicData,
    loading: isPublicPostLoading,
    error: publicError,
  } = useQuery(CARD_PUBLIC_POST_CONTENT_QUERY, {
    variables: { id: postId, shareSecret },
    skip: !isPublicView,
  });

  const post = isPublicView ? publicData?.publicPost : data?.post;
  const error = internalError ?? publicError;
  const isLoading = isInternalPostLoading || isPublicPostLoading;

  const content = post?.content.delta ? new Delta(post.content.delta) : null;
  const contentForCard = content
    ? prepareContentForCard(content, anchor)
    : null;
  const isTitleBlank = hasBlankTitle(contentForCard);

  const isPubliclyAccessible = post
    ? isAllowedThroughLinkAccess(
        PostPolicyAction.ViewPost,
        post,
        UserRole.Anonymous,
      )
    : false;

  const path =
    isPublicView && isPubliclyAccessible
      ? publicPostPath(post ?? { id: postId, title: '' }, { anchor })
      : postPath(post ?? { id: postId, title: '' }, { anchor });

  if ((!isLoading && !post) || error) {
    if (error?.message === 'FORBIDDEN') {
      return isLoggedIn ? (
        <RequestAccessCardError postId={postId} />
      ) : (
        <NotLoggedInCardError postId={postId} />
      );
    }

    return <NotFoundError />;
  }

  return (
    <CardContainer to={readOnly ? path : undefined}>
      <CardInnerContainer isTitleBlank={isTitleBlank}>
        {contentForCard && (
          <DeltaPreview
            maxLines={4}
            content={contentForCard}
            typography="card"
          />
        )}
      </CardInnerContainer>

      {post?.banner?.original && (
        <CardContentBanner url={post.banner.original} />
      )}
    </CardContainer>
  );
};

export default CardContent;
